import { BILLING_MODELS } from '../constants/billingModels';
import { SCHEDULE_CHAIN, scheduleFromServiceDate, agendaDaOpu } from '../constants/schedule';

/**
 * O cadastro de serviço — o que a tela de "Novo Serviço" cria e edita.
 *
 * Três coisas moram aqui, e as três são regra, não pintura:
 *
 *   `novoCadastro`    — como um serviço nasce (com a data de hoje, não vazio).
 *   `validarCadastro` — o que falta para poder gravar, em português.
 *   `montarCadastro`  — o que de fato desce para o banco.
 *
 * A terceira existe porque a tela gravava `formData` inteiro, e `formData`
 * carregava tudo o que a linha tinha (jsonb do workflow, carimbos, número) e
 * mais o que a tela inventava. Foi assim que dois campos sem coluna —
 * `scheduled_lab_notes` e `work_days` — passaram a derrubar o salvar com um
 * erro do PostgREST que ninguém entendia. Com a lista explícita, o que não é
 * do cadastro não viaja.
 */

/** Valor obrigatório em cada modelo de cobrança. `personalizado` não exige. */
export const VALOR_DO_MODELO = {
  [BILLING_MODELS.POR_TE]:          { campo: 'value_per_te',        rotulo: 'Valor por TE' },
  [BILLING_MODELS.POR_PRENHEZ]:     { campo: 'value_per_pregnancy', rotulo: 'Valor por prenhez' },
  [BILLING_MODELS.DIARIA]:          { campo: 'daily_value',         rotulo: 'Valor da diária' },
  [BILLING_MODELS.PACOTE_COMPLETO]: { campo: 'package_value',       rotulo: 'Valor do pacote' },
  [BILLING_MODELS.PERSONALIZADO]:   { campo: 'package_value',       rotulo: 'Valor do pacote', opcional: true },
  [BILLING_MODELS.POR_EMBRIAO]:     { campo: 'value_per_embryo',    rotulo: 'Valor por embrião' },
};

/**
 * Os campos que o cadastro é dono de. Só estes descem para o banco.
 *
 * As datas reais de OPU e FIV entram porque um serviço que HERDA a etapa de
 * outro copia a data em que ela aconteceu — é `lab_date` que ancora o prazo
 * da DG60. As demais datas reais são do workflow, e o cadastro não as toca.
 */
export const CAMPOS_DO_CADASTRO = [...new Set([
  // `local_id`: em qual dos locais do cliente o serviço acontece. Nulo é "o
  // primeiro" — o caso de quem tem um local só (`localDoServico`).
  'client_id', 'local_id', 'date', 'service_label', 'services_offered',
  'source_service_id', 'source_stage', 'opu_date', 'lab_date',
  'billing_model', 'billing_service_id',
  // Pacote e personalizado apontam para o mesmo campo; o Set tira a repetição.
  ...Object.values(VALOR_DO_MODELO).map(v => v.campo),
  'preco_vitrificacao', 'preco_dt',
  // Taxa da entrada por embrião. Vazio grava nulo, e nulo é "a do
  // laboratório" (`taxaDeEntrada`); zero é "este serviço não tem entrada".
  'entrada_por_embriao',
  ...SCHEDULE_CHAIN.flatMap(e => [e.field, e.notesField].filter(Boolean)),
  'sebrae', 'sebrae_projeto', 'embrioes_contratados',
  'contract_observations',
  // Responsável e ajudante de cada etapa (`utils/equipe`). Só desce quando a
  // tela mexeu nele — `montarCadastro` pula chave ausente.
  'equipe_das_etapas',
])];

const hoje = () => new Date().toISOString().slice(0, 10);

/**
 * Um serviço novo nasce datado de hoje: é o dia em que se está lançando.
 *
 * E com os preços padrão do laboratório (`precosParaServicoNovo`) já nos
 * campos. Só preenche: quem cadastra muda ali mesmo, e o que vale é o que for
 * salvo no serviço — mudar o padrão depois não mexe nele.
 */
export const novoCadastro = (precos = {}) => ({ ...precos, date: hoje(), services_offered: [] });

const vazio = (v) => v === null || v === undefined || v === '';

/**
 * As datas de etapa que ainda estão em branco, sugeridas a partir da data do
 * serviço para as etapas contratadas em `oferecidos`.
 *
 * Só o que está vazio. Marcar a TE num serviço que já tem a OPU agendada não
 * pode mover a OPU; e desmarcar e remarcar um tipo não pode apagar a data que
 * alguém acertou à mão. Para refazer tudo existe o botão de recalcular, que é
 * uma ação explícita.
 *
 * É isto que faz a agenda aparecer preenchida SEM o passo de "Recalcular
 * datas": na ordem natural do formulário a data vem antes dos tipos, e a
 * cascata a partir da data não tinha tipo nenhum para agendar.
 */
export const agendaQueFalta = (form = {}, oferecidos = form.services_offered || []) => {
  // A OPU, quando já tem dia, é a âncora: é por ela que a equipe marca, e o
  // protocolo cai oito dias antes dela, não no dia do serviço. `opu_no_dia` é
  // o dia clicado no calendário — fica só na tela, não é coluna, e espera o
  // tipo OPU ser marcado para virar data.
  const opu = oferecidos.includes('opu') && (form.opu_scheduled_date || form.opu_no_dia);
  const sugerida = opu
    ? agendaDaOpu(opu, oferecidos, { protocoloFeito: !!(form.protocolo_done || form.protocolo_date) })
    : scheduleFromServiceDate(form.date, oferecidos);
  return Object.fromEntries(
    Object.entries(sugerida).filter(([campo]) => vazio(form[campo])));
};

/**
 * Tipos cuja agenda some quando o tipo é desmarcado.
 *
 * SÓ ESTES DOIS, e não toda etapa descontratada. A data de OPU e de FIV num
 * serviço que não as contratou é, quase sempre, a data HERDADA da origem — e é
 * dela que a TE e a DG60 contam a gestação (`lab_scheduled_date` é a reserva
 * de `lab_date`). Apagá-la quebraria a conta de quem só faz TE. A da TE é o
 * relógio da entrada.
 *
 * Conferência (DG30/DG60) e protocolo não ancoram ninguém fora do próprio
 * tipo: desmarcados, as datas deles só ficavam paradas na agenda, aparecendo
 * no dashboard como etapa pendente de um serviço que não a tem.
 */
const AGENDA_QUE_SAI_COM_O_TIPO = ['pregnancy_check', 'protocolo'];

/**
 * As datas (e observações) agendadas de etapa cujo tipo não está contratado,
 * zeradas. Só a AGENDADA: a data real, se a etapa chegou a acontecer, é
 * história e fica.
 */
export const agendaQueSai = (oferecidos = []) => Object.fromEntries(
  SCHEDULE_CHAIN
    .filter(e => AGENDA_QUE_SAI_COM_O_TIPO.includes(e.requires) && !oferecidos.includes(e.requires))
    .flatMap(e => [e.field, e.notesField].filter(Boolean).map(campo => [campo, null])));

/**
 * O que falta para gravar. Lista vazia = pode salvar.
 *
 * Antes não havia nada aqui, e quem clicava em Salvar sem escolher a fazenda
 * lia "null value in column client_name violates not-null constraint". O
 * banco continua sendo a última linha de defesa; esta é a primeira, e fala a
 * língua de quem preenche.
 *
 * O valor do modelo é exigido porque o cálculo do serviço, sem ele, usava um
 * preço inventado (R$ 500 por TE, R$ 1.200 por prenhez). Serviço gravado sem
 * valor virava fatura com número que ninguém combinou.
 */
export const validarCadastro = (form = {}) => {
  const faltas = [];
  if (vazio(form.client_id)) faltas.push('o cliente');
  if (vazio(form.date)) faltas.push('a data do serviço');
  if (!(form.services_offered || []).length) faltas.push('ao menos um serviço');

  const modelo = form.billing_model;
  if (vazio(modelo)) {
    faltas.push('o modelo de cobrança');
  } else {
    const exige = VALOR_DO_MODELO[modelo];
    if (exige && !exige.opcional && !(parseFloat(form[exige.campo]) > 0)) {
      faltas.push(`${exige.rotulo.toLowerCase()} (maior que zero)`);
    }
  }

  // O responsável de cada etapa NÃO é exigido aqui: no cadastro, muitas vezes
  // ainda não se sabe quem vai. Quem cobra é a conclusão da etapa, na aba dela.
  return faltas;
};

/**
 * O payload que desce para o banco.
 *
 * Só os campos do cadastro; `''` vira `null` (um numérico vazio não passa pelo
 * Postgres); `sebrae` é sempre booleano (a coluna é `not null`); e a etapa
 * herdada sem serviço de origem cai — o banco cobra o par numa constraint, e
 * a tela guarda a etapa antes de haver origem, que é um meio-caminho legítimo
 * na tela mas não na linha.
 */
export const montarCadastro = (form = {}, { clientName = '' } = {}) => {
  const linha = {};
  CAMPOS_DO_CADASTRO.forEach((campo) => {
    if (!(campo in form)) return;
    linha[campo] = vazio(form[campo]) ? null : form[campo];
  });

  // Desmarcou a conferência ou o protocolo: a agenda deles vai junto, mesmo
  // que a tela não tenha limpado (serviço salvo antes desta regra). Só quando
  // a lista de tipos veio: sem ela, "não contratado" seria um chute, e o chute
  // apagaria a agenda de DG de quem a tem.
  if (Array.isArray(form.services_offered)) {
    Object.assign(linha, agendaQueSai(form.services_offered));
  }

  linha.sebrae = !!form.sebrae;
  linha.client_name = clientName;
  if (!linha.source_service_id) {
    linha.source_service_id = null;
    linha.source_stage = null;
  }
  return linha;
};
